import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Truck, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getPriceData } from '../utils/currency';

const FREE_DELIVERY_THRESHOLD = 25; // 25€ for free delivery
const MINIMUM_ORDER_AMOUNT = 15.00; // Minimum order in EUR

const CartPage = () => {
    const { cart, updateQuantity, removeFromCart, loading } = useCart();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const handleCheckout = () => {
        // Allow both guests and logged-in users to proceed to checkout
        navigate('/checkout');
    };

    // Calculate progress to free delivery
    const progressToFreeDelivery = Math.min((cart.total / FREE_DELIVERY_THRESHOLD) * 100, 100);
    const remainingForFreeDelivery = Math.max(FREE_DELIVERY_THRESHOLD - cart.total, 0);
    const hasFreeDelivery = cart.total >= FREE_DELIVERY_THRESHOLD;

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="container-main py-8">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 w-48 bg-muted rounded" />
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-32 bg-muted rounded" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (cart.items.length === 0) {
        return (
            <div className="min-h-screen bg-background">
                <div className="container-main py-16">
                    <div className="text-center max-w-md mx-auto">
                        <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                            <ShoppingBag className="h-12 w-12 text-muted-foreground" />
                        </div>
                        <h1 className="font-serif text-3xl font-bold mb-4 text-[#6B1028]">Количката е празна</h1>
                        <p className="text-muted-foreground mb-8">
                            Все още нямате продукти в количката. Разгледайте нашите продукти и добавете нещо вкусно!
                        </p>
                        <Link to="/products">
                            <Button className="btn-primary" data-testid="continue-shopping-btn">
                                Към продуктите
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="container-main py-8">
                <h1 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-[#6B1028]">Количка</h1>

                {/* Free Delivery Progress Bar */}
                <div className="bg-white rounded-xl border-2 border-amber-200 p-5 mb-8 shadow-sm" data-testid="free-delivery-banner">
                    <div className="flex items-center gap-3 mb-3">
                        {hasFreeDelivery ? (
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                <Gift className="h-5 w-5 text-green-600" />
                            </div>
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <Truck className="h-5 w-5 text-amber-600" />
                            </div>
                        )}
                        <div className="flex-1">
                            {hasFreeDelivery ? (
                                <p className="font-bold text-green-700">
                                    🎉 Поздравления! Имате безплатна доставка!
                                </p>
                            ) : (
                                <p className="font-semibold text-[#6B1028]">
                                    Добави още <span className="text-amber-600 font-bold">{getPriceData(remainingForFreeDelivery).eurFormatted} €</span> за безплатна доставка!
                                </p>
                            )}
                        </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="relative h-3 bg-amber-100 rounded-full overflow-hidden">
                        <div 
                            className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
                            style={{ 
                                width: `${progressToFreeDelivery}%`,
                                background: hasFreeDelivery 
                                    ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)' 
                                    : 'linear-gradient(90deg, #D4A500 0%, #F5C400 50%, #D4A500 100%)'
                            }}
                        />
                        {/* Milestone markers */}
                        <div className="absolute inset-0 flex items-center justify-end pr-2">
                            <span className="text-[10px] font-bold text-amber-800">{FREE_DELIVERY_THRESHOLD}€</span>
                        </div>
                    </div>
                    
                    {!hasFreeDelivery && (
                        <p className="text-xs text-[#6B1028]/60 mt-2">
                            Безплатна доставка за цяла София за поръчки над {FREE_DELIVERY_THRESHOLD}€
                        </p>
                    )}
                </div>

                {/* Quantity tier upsell suggestions */}
                {cart.tier_suggestions?.map(s => (
                    <div key={s.product_id} className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-wrap items-center gap-3 justify-between" data-testid={`tier-suggestion-${s.product_id}`}>
                        <p className="text-sm text-emerald-800">
                            🏷 Добавете още <strong>{s.add_qty} бр.</strong> от „{s.name}" и цената ще стане <strong>{getPriceData(s.new_unit_price).eurFormatted} € на брой</strong>!
                        </p>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => updateQuantity(s.product_id, s.target_qty)}
                            data-testid={`tier-suggestion-btn-${s.product_id}`}>
                            Добави още {s.add_qty} и активирай офертата
                        </Button>
                    </div>
                ))}

                {cart.items.some(i => i.requires_prepayment_outside_sofia) && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="cart-prepayment-warning">
                        <p className="text-sm text-blue-800">
                            🧊 <strong>Важно:</strong> Количката ви съдържа месни или млечни продукти, които се съхраняват в хладилник. При доставка извън София тези продукти се изпращат само след предварително плащане (карта или банков превод) — наложен платеж не е достъпен, тъй като непотърсените пратки могат да останат в офис на Еконт и храната да се развали.
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cart.items.map((item) => (
                            <div 
                                key={item.product_id} 
                                className="bg-white rounded-lg border border-amber-200 p-4 flex gap-4 hover:shadow-md transition-shadow"
                                data-testid={`cart-item-${item.product_id}`}
                            >
                                <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                                    <img 
                                        src={item.image_url} 
                                        alt={item.name}
                                        className="w-24 h-24 object-cover rounded-md"
                                    />
                                </Link>
                                
                                <div className="flex-1 min-w-0">
                                    <Link to={`/product/${item.product_id}`}>
                                        <h3 className="font-semibold text-[#6B1028] hover:text-amber-600 transition-colors line-clamp-2">
                                            {item.name}
                                        </h3>
                                    </Link>
                                    
                                    {/* Variations */}
                                    {item.selected_variations && Object.keys(item.selected_variations).length > 0 && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {Object.entries(item.selected_variations).map(([key, value]) => `${key}: ${value}`).join(', ')}
                                        </p>
                                    )}
                                    
                                    <div className="flex items-center justify-between mt-4">
                                        {/* Quantity */}
                                        <div className="flex items-center border border-amber-200 rounded-md">
                                            <button
                                                onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                                className="p-2 hover:bg-amber-50 transition-colors text-[#6B1028]"
                                                data-testid={`cart-minus-${item.product_id}`}
                                            >
                                                <Minus className="h-4 w-4" />
                                            </button>
                                            <span className="w-10 text-center text-sm font-semibold text-[#6B1028]">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                                className="p-2 hover:bg-amber-50 transition-colors text-[#6B1028]"
                                                disabled={item.quantity >= item.stock}
                                                data-testid={`cart-plus-${item.product_id}`}
                                            >
                                                <Plus className="h-4 w-4" />
                                            </button>
                                        </div>
                                        
                                        {/* Price & Remove */}
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <p className="font-bold text-amber-600">
                                                    {getPriceData(item.price * item.quantity).eurFormatted} €
                                                </p>
                                                {item.original_price > item.price && (
                                                    <p className="text-xs text-muted-foreground line-through">
                                                        {getPriceData(item.original_price * item.quantity).eurFormatted} €
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.product_id)}
                                                className="p-2 text-muted-foreground hover:text-[#6B1028] transition-colors"
                                                data-testid={`cart-remove-${item.product_id}`}
                                            >
                                                <Trash2 className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl border-2 border-amber-200 p-6 sticky top-32 shadow-sm">
                            <h2 className="font-serif text-xl font-bold mb-6 text-[#6B1028]">Обобщение</h2>
                            
                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between">
                                    <span className="text-[#6B1028]/70">Продукти ({cart.items.length})</span>
                                    <div className="text-right">
                                        <span className="font-semibold text-[#6B1028]">{getPriceData(cart.total).eurFormatted} €</span>
                                    </div>
                                </div>
                                {cart.discounts?.length > 0 && cart.discounts.map((d, i) => (
                                    <div key={i} className="flex justify-between text-sm" data-testid={`cart-discount-${d.promo_id}`}>
                                        <span className="text-green-700 truncate mr-2">🎁 {d.label}</span>
                                        <span className="text-green-700 font-semibold whitespace-nowrap">-{getPriceData(d.amount).eurFormatted} €</span>
                                    </div>
                                ))}
                                {cart.discount_total > 0 && (
                                    <div className="flex justify-between text-sm font-semibold border-t border-dashed border-green-300 pt-2">
                                        <span className="text-green-700">Обща отстъпка</span>
                                        <span className="text-green-700" data-testid="cart-discount-total">-{getPriceData(cart.discount_total).eurFormatted} €</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-[#6B1028]/70">Доставка</span>
                                    {hasFreeDelivery ? (
                                        <span className="text-green-600 font-semibold">Безплатна ✓</span>
                                    ) : (
                                        <span className="text-[#6B1028]/70">Изчислява се</span>
                                    )}
                                </div>
                            </div>
                            
                            <div className="border-t-2 border-amber-200 pt-4 mb-6">
                                <div className="flex justify-between text-lg font-bold">
                                    <span className="text-[#6B1028]">Общо</span>
                                    <div className="text-right">
                                        <span className="text-amber-600" data-testid="cart-total">{getPriceData(cart.total).eurFormatted} €</span>
                                    </div>
                                </div>
                                
                                {/* Minimum order warning */}
                                {cart.total < MINIMUM_ORDER_AMOUNT && cart.total > 0 && (
                                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                        <p className="text-sm text-amber-800">
                                            ⚠️ Минималната сума за поръчка е <strong>{getPriceData(MINIMUM_ORDER_AMOUNT).eurFormatted} €</strong>
                                        </p>
                                        <p className="text-xs text-amber-600 mt-1">
                                            Добавете още {getPriceData(MINIMUM_ORDER_AMOUNT - cart.total).eurFormatted} € към кошницата
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            <Button 
                                onClick={handleCheckout}
                                disabled={cart.total < MINIMUM_ORDER_AMOUNT}
                                className="btn-gold w-full text-[#6B1028] disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="checkout-btn"
                            >
                                Продължи към плащане
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                            
                            <Link to="/products">
                                <Button variant="ghost" className="w-full mt-3 text-[#6B1028] hover:bg-amber-50">
                                    Продължи пазаруването
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartPage;
